import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { CheckActivityDef } from 'Src/feature/check/models';
import { useOrderType } from 'Src/feature/order-type/useOrderType';

interface CheckActivityStatementProps {
  activity: CheckActivityDef;
}

const CheckActivityStatement: FC<CheckActivityStatementProps> = ({ activity }) => {
  const { t } = useTranslation();

  const orderTypeList = useOrderType.query.list();

  const { type, surchargeInfo, status, info, checksWithItems } = activity;

  // if info is populated, it will come back as a string formatted like:
  // - "key: value", where key can be partySize or labelPrefix, OR 
  // - a string containing either the order type name OR unknown info
  const [infoField] = (info ?? '').split(': ');

  return (
    <Copy variant="body" size="sm" ellipsis>
      {
        (() => {
          switch (type) {
            case 'ItemAdded': return t('check.activities.itemAdded');
            case 'ItemEdited': return t('check.activities.itemEdited');
            case 'ItemRemoved': return t('check.activities.itemRemoved');
            case 'ItemSent': return t('check.activities.itemSent');
            case 'ItemCompleted': return t('check.activities.itemCompleted');
            case 'ItemVoided': return t('check.activities.itemVoided');
            case 'ItemRepeated': return t('check.activities.itemRepeated');
            case 'ItemIndefiniteHold': return t('check.activities.itemIndefiniteHold');
            case 'ItemIndefiniteHoldRelease': return t('check.activities.itemIndefiniteHoldRelease');
            case 'Created': return t('check.activities.created');
            case 'Closed': return t('check.activities.closed');
            case 'Voided': return t('check.activities.voided');
            case 'Reopened': return t('check.activities.reopened');
            case 'AutoDiscountAdded': return t('check.activities.autoDiscountAdded');
            case 'DiscountAutoDisabled': return t('check.activities.discountAutoDisabled');
            case 'DiscountAutoEnabled': return t('check.activities.discountAutoEnabled');
            case 'DiscountAdded': return t('check.activities.discountAdded');
            case 'DiscountRemoved': return t('check.activities.discountRemoved');
            case 'DiscountEnabled': return t('check.activities.discountEnabled');
            case 'DiscountDisabled': return t('check.activities.discountDisabled');
            case 'PaymentAdded': {
              switch (status) {
                case 'Declined': return t('check.activities.paymentAdded.declined');
                case 'Success':
                default: return t('check.activities.paymentAdded.default');
              }
            }
            case 'PaymentUpdated': {
              switch (status) {
                case 'Declined': return t('check.activities.paymentUpdated.declined');
                case 'Success':
                default: return t('check.activities.paymentUpdated.default');
              }
            }
            case 'PaymentRemoved': {
              switch (status) {
                case 'Declined': return t('check.activities.paymentRemoved.declined');
                case 'Success':
                default: return t('check.activities.paymentRemoved.default');
              }
            }
            case 'ManualCreditCardPaymentInitialized': return t('check.activities.manualPaymentRequest');
            case 'CreditCardVerified': return t('check.activities.cardVerify');
            case 'InfoEdited': {
              // can't use switch/case here because info field is just a string that the client needs to parse, thus no type
              if (infoField === 'partySize') return t('check.activities.infoEdited.partySize');
              if (infoField === 'labelPrefix') return t('check.activities.infoEdited.labelPrefix');
              // order type update does not come back from the server as "key: value"; it only returns the name of the updated order type
              if ((orderTypeList.data ?? []).find(({ name }) => name === infoField)) return t('check.activities.infoEdited.orderType');
              return t('check.activities.infoEdited.default');
            }
            case 'CustomerUpdated': return t('check.activities.customerUpdated');
            case 'Transferred': return t('check.activities.transferred');
            case 'SplitInto': return t('check.activities.splitInto');
            case 'SplitFrom': return t('check.activities.splitFrom');
            case 'MergedInto': {
              if (checksWithItems && checksWithItems.length > 0 && checksWithItems[0].targetCheckLabel) {
                return t('check.activities.mergedIntoCheck', { checkNumber: checksWithItems[0].targetCheckLabel });
              }
              return t('check.activities.mergedInto');
            };
            case 'MergedFrom': {
              if (checksWithItems && checksWithItems.length > 0 && checksWithItems[0].targetCheckLabel) {
                return t('check.activities.mergedFromCheck', { checkNumber: checksWithItems[0].targetCheckLabel });
              }
              return t('check.activities.mergedFrom');
            };
            case 'AutoSurchargeAdded': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.autoSurchargeAdded.gratuity');
                case false: return t('check.activities.autoSurchargeAdded.fee');
                default: return t('check.activities.autoSurchargeAdded.default');
              }
            }
            case 'SurchargeAdded': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeAdded.gratuity');
                case false: return t('check.activities.surchargeAdded.fee');
                default: return t('check.activities.surchargeAdded.default');
              }
            }
            case 'SurchargeUpdated': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeUpdated.gratuity');
                case false: return t('check.activities.surchargeUpdated.fee');
                default: return t('check.activities.surchargeUpdated.default');
              }
            }
            case 'SurchargeRemoved': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeRemoved.gratuity');
                case false: return t('check.activities.surchargeRemoved.fee');
                default: return t('check.activities.surchargeRemoved.default');
              }
            }
            case 'SurchargeAutoEnabled': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeAutoEnabled.gratuity');
                case false: return t('check.activities.surchargeAutoEnabled.fee');
                default: return t('check.activities.surchargeAutoEnabled.default');
              }
            }
            case 'SurchargeEnabled': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeEnabled.gratuity');
                case false: return t('check.activities.surchargeEnabled.fee');
                default: return t('check.activities.surchargeEnabled.default');
              }
            }
            case 'SurchargeAutoDisabled': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeAutoDisabled.gratuity');
                case false: return t('check.activities.surchargeAutoDisabled.fee');
                default: return t('check.activities.surchargeAutoDisabled.default');
              }
            }
            case 'SurchargeDisabled': {
              switch (surchargeInfo?.isGratuity) {
                case true: return t('check.activities.surchargeDisabled.gratuity');
                case false: return t('check.activities.surchargeDisabled.fee');
                default: return t('check.activities.surchargeDisabled.default');
              }
            }
            case 'GiftCardAdded': return t('check.activities.giftCardAdded');
            case 'GiftCardRemoved': return t('check.activities.giftCardRemoved');
            case 'GiftCardIssued': return t('check.activities.giftCardIssued');
            case 'GiftCardIssueVoided': return t('check.activities.giftCardIssueVoided');
            case 'GiftCardReloaded': return t('check.activities.giftCardReloaded');
            case 'GiftCardReloadVoided': return t('check.activities.giftCardReloadVoided');
            case 'GoodFaithPaymentReran': {
              switch (status) {
                case 'Declined': return t('check.activities.goodFaithPaymentReran.declined');
                case 'Success':
                default: return t('check.activities.goodFaithPaymentReran.default');
              }
            }
            default: return t('check.activities.unknown');
          }
        })()
      }
    </Copy>
  );
};

export type { CheckActivityStatementProps };
export { CheckActivityStatement };
